/**
 * @nanokart/tryon-studio-sdk
 * Default Host Adapter
 * 
 * Basic implementation that routes operations to callbacks.
 * Used as base class for platform-specific adapters.
 */

import {
  HostAdapter,
  HostAdapterCallbacks,
  HostEnvironment,
  AddToCartIntent,
  CommunicationMode,
} from './types';
import { AnalyticsEvent } from '../../types/events';

export class DefaultHostAdapter implements HostAdapter {
  protected callbacks: HostAdapterCallbacks;
  public communicationMode: CommunicationMode;
  public targetOrigin?: string;

  constructor(
    callbacks: HostAdapterCallbacks = {},
    options?: {
      communicationMode?: CommunicationMode;
      targetOrigin?: string;
    }
  ) {
    this.callbacks = callbacks;
    this.communicationMode = options?.communicationMode ?? 'direct';
    this.targetOrigin = options?.targetOrigin;
  }

  async addToCart(intent: AddToCartIntent): Promise<void> {
    if (this.communicationMode === 'postMessage' && this.targetOrigin) {
      window.parent.postMessage(
        { type: 'NANOKART_ADD_TO_CART', payload: intent },
        this.targetOrigin
      );
    }

    // Call callback if provided
    await this.callbacks.onAddToCart?.(intent);
  }

  async removeFromCart(productId: string, variantId?: string): Promise<void> {
    if (this.communicationMode === 'postMessage' && this.targetOrigin) {
      window.parent.postMessage(
        { type: 'NANOKART_REMOVE_FROM_CART', payload: { productId, variantId } },
        this.targetOrigin
      );
    }

    await this.callbacks.onRemoveFromCart?.(productId, variantId);
  }

  track(event: AnalyticsEvent): void {
    if (this.communicationMode === 'postMessage' && this.targetOrigin) {
      window.parent.postMessage(
        { type: 'NANOKART_ANALYTICS', payload: event },
        this.targetOrigin
      );
    }

    this.callbacks.onAnalyticsEvent?.(event);
  }

  openCheckout(): void {
    if (this.communicationMode === 'postMessage' && this.targetOrigin) {
      window.parent.postMessage(
        { type: 'NANOKART_OPEN_CHECKOUT', payload: {} },
        this.targetOrigin
      );
    }

    this.callbacks.onOpenCheckout?.();
  }

  async resolveProductData(_productId: string): Promise<Record<string, unknown> | null> {
    // Default: no additional data
    return null;
  }

  getHostEnv(): HostEnvironment {
    return {
      isIframe: typeof window !== 'undefined' && window !== window.parent,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en-US',
      currency: 'INR', // Default currency
      timezone:
        typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'Asia/Kolkata',
    };
  }
}
